import type { NavMoreMenuItemProps, NavMoreMenuProps } from './NavMoreMenu.types';
import type { ReactElement } from 'react';

import { Badge, Flex, Heading, Separator, Text } from '@rango-dev/whale';
import clsx from 'clsx';
import React, { Fragment } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import styles from './NavMoreMenu.module.css';

function NavMoreMenuItem(props: NavMoreMenuItemProps): ReactElement {
  const { link, onNavigate } = props;
  const isExternal = link.location.startsWith('http');
  const className = clsx(styles.item, link.className);

  const handleClick = () => {
    link.onClick?.();
    onNavigate?.();
  };

  const label = (
    <Text size="3" weight="medium">
      {link.title}
    </Text>
  );

  const badge = link.badge ? (
    <Badge className={link.badge.className} color={link.badge.color}>
      {link.badge.title}
    </Badge>
  ) : null;

  return isExternal ? (
    <a
      id={link.id}
      className={className}
      href={link.location}
      target="_blank"
      rel="noreferrer"
      onClick={handleClick}>
      {label}
      {badge}
    </a>
  ) : (
    <RouterLink id={link.id} className={className} to={link.location} onClick={handleClick}>
      {label}
      {badge}
    </RouterLink>
  );
}

export function NavMoreMenu(props: NavMoreMenuProps): ReactElement {
  const { groups, onNavigate } = props;

  return (
    <Flex direction="column" gap="4">
      {groups.map((group, index) => (
        <Fragment key={group.id}>
          {index > 0 && <Separator size="4" className={styles.separator} />}
          <Flex asChild direction="column" gap="4">
            <section aria-labelledby={group.id}>
              <Heading
                id={group.id}
                as="h2"
                size="3"
                weight="bold"
                align="left"
                className={styles.groupHeading}>
                {group.title}
              </Heading>
              <Flex asChild direction="column" gap="4">
                <ul className={styles.list}>
                  {group.links.map((link) => (
                    <li key={link.id}>
                      <NavMoreMenuItem link={link} onNavigate={onNavigate} />
                    </li>
                  ))}
                </ul>
              </Flex>
            </section>
          </Flex>
        </Fragment>
      ))}
    </Flex>
  );
}
